import { DownloadOutlined, DownOutlined } from "@ant-design/icons";
import { Badge, Flex, Button, Select, Tooltip, Dropdown } from "antd";
import Search from "antd/es/input/Search";
import React, { useEffect, useRef, useState } from "react";
import { JobApplicationFilters } from "../../../../Interface";
import {
  JobApplication,
  JobApplicationStatus,
} from "../../../../Entities/JobApplication";
import { ConnectedApp } from "../../../../Interface";
import appIntegrationService from "../../../../Service/app.integration.service";
import ATSImportModal from "./ATSImportModal";
import { JobDescriptionEntity } from "../../../../Entities/JobDescription";
import "./JobApplicationFilterMenu.scss";

interface JobApplicationFilterMenuProps {
  updateFilter: (key: keyof JobApplicationFilters, value: any) => void;
  filters: JobApplicationFilters;
  clearFilters: () => void;
  jobPost: JobDescriptionEntity;
  onExport?: () => void;
  exportDisabled?: boolean;
  selectedCount?: number;
  handleSendBulkEmail: () => Promise<void>;
  jobPostActive: boolean;
  handleBulkStatusChange?: (status: JobApplicationStatus) => void;
  selectedRows?: JobApplication[];
  atsImportOnly?: boolean;
  handleBulkReportDownload: () => void;
}

const JobApplicationFilterMenu: React.FC<JobApplicationFilterMenuProps> = ({
  updateFilter,
  filters,
  clearFilters,
  jobPost,
  onExport,
  exportDisabled = true,
  selectedCount = 0,
  handleSendBulkEmail,
  jobPostActive,
  handleBulkStatusChange,
  selectedRows = [],
  atsImportOnly = false,
  handleBulkReportDownload,
}) => {
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([]);
  const [selectedATS, setSelectedATS] = useState<
    "zoho" | "lever" | "custom" | null
  >(null);

  const handleSortFilter = (value: string | undefined) => {
    updateFilter("sort", value || null);
  };

  const handleSearch = (value: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    updateFilter("search", value || null);
  };

  const handleClearAll = () => {
    clearFilters();
    setSearchText(undefined);
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(
    (value) => value !== null && value !== undefined && value !== "",
  );

  // Get active filter count (excluding search and sort)
  const getActiveFilterCount = () => {
    const statusFilters = [
      "status",
      "uploadType",
      "profileMatchSatus",
      "roundStatus",
      "interviewStatus",
      "sort",
      "search",
    ];
    return statusFilters.filter((key) => {
      const value = filters[key as keyof JobApplicationFilters];
      return value !== null && value !== undefined && value !== "";
    }).length;
  };

  const activeFilterCount = getActiveFilterCount();

  const handleATSAppClick = (type: "zoho" | "lever") => {
    setSelectedATS(type);
  };

  useEffect(() => {
    setSearchText(filters.search || "");
  }, [filters.search]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      updateFilter("search", searchText || null);
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText]);

  useEffect(() => {
    const fetchConnectedApps = async () => {
      const response = await appIntegrationService.getAllConnectedApps();
      if (!response?.success) {
        return;
      }
      setConnectedApps(response.data);
    };
    fetchConnectedApps();
  }, []);

  if (atsImportOnly) {
    const hasZoho = connectedApps.some((c) => c.appType === "zoho");
    const hasLever = connectedApps.some((c) => c.appType === "lever");

    if (!hasZoho && !hasLever) return null;

    return (
      <>
        <Dropdown
          menu={{
            items: [
              {
                key: "zoho",
                label: (
                  <div className="ats-menu-item">
                    <Badge
                      status={hasZoho ? "success" : "error"}
                      text="Zoho Recruit"
                    />
                  </div>
                ),
                onClick: () => handleATSAppClick("zoho"),
                disabled: !hasZoho,
              },
              {
                key: "lever",
                label: (
                  <div className="ats-menu-item">
                    <Badge
                      status={hasLever ? "success" : "error"}
                      text="Lever Hire"
                    />
                  </div>
                ),
                onClick: () => handleATSAppClick("lever"),
                disabled: !hasLever,
              },
            ],
          }}
          trigger={["click", "hover"]}
          placement="bottomLeft"
          overlayClassName="ats-dropdown-overlay"
        >
          <Button size="middle" aria-label="Import from ATS systems">
            Import from ATS
            <DownOutlined className="dropdown-arrow" />
          </Button>
        </Dropdown>
        {selectedATS && (
          <ATSImportModal
            selectedATS={selectedATS}
            open={selectedATS !== null}
            onClose={() => setSelectedATS(null)}
            jobPost={jobPost}
          />
        )}
      </>
    );
  }

  return (
    <div className="job-application-filter-menu">
      <Flex
        gap={8}
        className="job-application-filter-menu__filters"
        align="center"
        justify="space-between"
      >
        <Badge count={activeFilterCount}>
          <Button
            className="clear-all-button"
            type="default"
            size="middle"
            disabled={!hasActiveFilters}
            onClick={handleClearAll}
          >
            Clear All
          </Button>
        </Badge>
        <div className="job-application-filter-menu__actions">
          {selectedCount === 0 && (
            <>
              <div className="job-application-filter-menu__search-wrapper">
                <div className="job-application-filter-menu__search">
                  <Search
                    allowClear={{ clearIcon: true }}
                    size="middle"
                    placeholder="Search by name or email"
                    enterButton
                    onSearch={handleSearch}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>
              <Select
                className="filter-select"
                size="middle"
                allowClear
                placeholder="Sort by"
                options={[
                  {
                    value: "latest_first",
                    label: (
                      <Tooltip
                        title="Show most recent applications first"
                        placement="bottomRight"
                      >
                        <span>Latest first</span>
                      </Tooltip>
                    ),
                  },
                  {
                    value: "oldest_first",
                    label: (
                      <Tooltip
                        title="Show oldest applications first"
                        placement="bottomRight"
                      >
                        <span>Oldest first</span>
                      </Tooltip>
                    ),
                  },
                  {
                    value: "profile_score_highest",
                    label: (
                      <Tooltip
                        title="Show highest profile match scores first"
                        placement="bottomRight"
                      >
                        <span>Profile score highest</span>
                      </Tooltip>
                    ),
                  },
                  {
                    value: "profile_score_lowest",
                    label: (
                      <Tooltip
                        title="Show lowest profile match scores first"
                        placement="bottomRight"
                      >
                        <span>Profile score lowest</span>
                      </Tooltip>
                    ),
                  },
                ]}
                value={filters.sort}
                onChange={handleSortFilter}
              />

              {connectedApps.length > 0 &&
                jobPost?.isActive &&
                jobPost.appJobId && (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "key",
                          label: (
                            <div className="ats-menu-item">
                              <Badge
                                status={
                                  connectedApps.find(
                                    (c) => c.appType === "zoho",
                                  )
                                    ? "success"
                                    : "error"
                                }
                                text={"Zoho Recruit"}
                              />
                            </div>
                          ),
                          onClick: () => handleATSAppClick("zoho"),
                          disabled: connectedApps.find(
                            (c) => c.appType === "zoho",
                          )
                            ? false
                            : true,
                        },
                        {
                          key: "key",
                          label: (
                            <div className="ats-menu-item">
                              <Badge
                                status={
                                  connectedApps.find(
                                    (c) => c.appType === "lever",
                                  )
                                    ? "success"
                                    : "error"
                                }
                                text={":Lever Hire"}
                              />
                            </div>
                          ),
                          onClick: () => handleATSAppClick("lever"),
                          disabled: connectedApps.find(
                            (c) => c.appType === "lever",
                          )
                            ? false
                            : true,
                        },
                      ],
                    }}
                    trigger={["click", "hover"]}
                    placement="bottomLeft"
                    overlayClassName="ats-dropdown-overlay"
                  >
                    <Button size="middle" aria-label="Import from ATS systems">
                      Import from ATS
                      <DownOutlined className="dropdown-arrow" />
                    </Button>
                  </Dropdown>
                )}

              {onExport && (
                <Tooltip title="Download all applications as CSV">
                  <Button
                    type="default"
                    size="middle"
                    onClick={onExport}
                    disabled={exportDisabled}
                    className="export-button"
                  >
                    Export
                  </Button>
                </Tooltip>
              )}
            </>
          )}

          {selectedCount > 0 && jobPostActive && (
            <>
              <div style={{ display: "flex", gap: 0 }}>
                <Tooltip title="Move selected candidates to shortlist">
                  <Button
                    type="default"
                    size="middle"
                    onClick={() => handleBulkStatusChange?.("shortlisted")}
                    style={{ borderRadius: "4px 0 0 4px" }}
                  >
                    Shortlist
                  </Button>
                </Tooltip>
                <Tooltip title="Reject selected candidates">
                  <Button
                    type="default"
                    size="middle"
                    onClick={() => handleBulkStatusChange?.("rejected")}
                    style={{ borderRadius: 0, borderLeftWidth: 0 }}
                  >
                    Reject
                  </Button>
                </Tooltip>
                <Tooltip title="Mark selected candidates as hired">
                  <Button
                    type="default"
                    size="middle"
                    onClick={() => handleBulkStatusChange?.("hired")}
                    style={{ borderRadius: "0 4px 4px 0", borderLeftWidth: 0 }}
                  >
                    Hired
                  </Button>
                </Tooltip>
              </div>

              <Tooltip title="Send reminders to selected candidates based on their current stage (resume upload, assessment, or interview slot)">
                <Button
                  type="default"
                  size="middle"
                  onClick={handleSendBulkEmail}
                  style={{ borderRadius: "4px" }}
                >
                  Send Reminder
                </Button>
              </Tooltip>

              {handleBulkReportDownload && (
                <Tooltip title="Download AI summary report for selected applications">
                  <Button
                    type="default"
                    size="middle"
                    onClick={handleBulkReportDownload}
                    className="export-button"
                    icon={<DownloadOutlined />}
                    iconPosition="end"
                  >
                    Download Report {selectedCount > 0 && `(${selectedCount})`}
                  </Button>
                </Tooltip>
              )}
              {onExport && (
                <Tooltip title="Download selected applications as CSV">
                  <Button
                    type="default"
                    size="middle"
                    onClick={onExport}
                    disabled={exportDisabled}
                    className="export-button"
                  >
                    Export
                  </Button>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </Flex>

      {selectedATS && (
        <ATSImportModal
          selectedATS={selectedATS}
          open={selectedATS !== null}
          onClose={() => setSelectedATS(null)}
          jobPost={jobPost}
        />
      )}
    </div>
  );
};

export default JobApplicationFilterMenu;
